'use client';

import { Carousel } from '@packages/shared/src/ui/Carousel/Carousel/Carousel';
import { defineMessages, useIntl } from 'react-intl';
import { useContext } from 'react';
import type { ParagraphType, RecognizerComponentProps } from '../../../interfaces';
import { ComponentTypes } from '../../../interfaces';
import { useContentFilters } from '../../hooks/useContentFilters';
import { ComponentRecognizer } from '../ComponentRecognizer/ComponentRecognizer.client';
import { registerComponents } from '../ComponentRecognizer/componentRegistry';
import type { CarouselType } from './types';
import { classes } from '../../utils/classes';
import { ContentAlreadyFilteredContext } from '../ComponentRecognizer/ContentAlreadyFilteredContext.client';

const messages = defineMessages({
  ariaLabelPrev: {
    id: 'navigation.carousel.prev.aria-label',
    defaultMessage: 'Zurück',
  },
  ariaLabelNext: {
    id: 'navigation.carousel.next.aria-label',
    defaultMessage: 'Vor',
  },
  buttonAutoplayPause: {
    id: 'navigation.carousel.pause.aria-label',
    defaultMessage: 'Automatische Slideshow anhalten',
  },
  buttonAutoplayStart: {
    id: 'navigation.carousel.start.aria-label',
    defaultMessage: 'Automatische diashow starten',
  },
  slideAriaLabel: {
    id: 'navigation.carousel.slide.aria-label',
    defaultMessage: 'Bild {i} von {n}',
  },
  galleryAriaLabel: {
    id: 'navigation.carousel.gallery.aria-label',
    defaultMessage: 'Bildergalerie',
  },
});

/**
 * Represents the Carousel component used in CMS.
 * This component displays a sequence of items (paragraphs) with various configuration options such as autoplay, loop, and slide visibility.
 *
 * @param {CarouselType} data - The data object containing the carousel configuration and content.
 * @returns A Carousel component with customized settings and content.
 */
export const CmsCarousel = ({
  data: {
    field_autoplay: autoplay = false,
    field_infinite: infinite = false,
    field_speed: duration,
    field_margin: margin = '0 0 2rem 0',
    field_padding: padding = '0',
    field_slides_to_show: slidesToShow = 1,
    paragraph,
  },
  className,
}: RecognizerComponentProps<CarouselType>) => {
  const { formatMessage } = useIntl();
  /**
    TODO: Remove filtering here after audience filter is used via filterParagraphs in CMS page.

    Currently the CMS page needs this filter here, but the preview page already filters via filterParagraph.
    A double filter would result in both filtering via preview settings and the actual ecState cookie which results
    in overfiltered content.
  */
  const contentIsAlreadyFiltered = useContext(ContentAlreadyFilteredContext);
  const filters = useContentFilters({
    withAudienceControl: !contentIsAlreadyFiltered,
    withTimeControl: !contentIsAlreadyFiltered,
  });

  return (
    <div style={{ margin, padding }} className={classes('[& button]:absolute relative', className)}>
      <Carousel
        aria={{
          buttonPrev: formatMessage(messages.ariaLabelPrev),
          buttonNext: formatMessage(messages.ariaLabelNext),
          buttonAutoplayPause: formatMessage(messages.buttonAutoplayPause),
          buttonAutoplayStart: formatMessage(messages.buttonAutoplayStart),
          slideAriaLabel: (i, n) => formatMessage(messages.slideAriaLabel, { i: i + 1, n }),
          galleryAriaLabel: formatMessage(messages.galleryAriaLabel),
        }}
        arrowsInsideBoundary
        autoplay={autoplay}
        loop={infinite}
        visibleSlides={slidesToShow}
        {...(duration && { emblaOptions: { duration } })}
      >
        {paragraph
          ?.filter((item) => filters.every((filter) => filter(item)))
          ?.map((item: ParagraphType) => (
            <ComponentRecognizer key={item.id} item={item} />
          ))}
      </Carousel>
    </div>
  );
};

// Needed to run this component in Storybook and Jest tests. Register the component in a global componentRegistry object
registerComponents({
  [ComponentTypes.Carousel]: CmsCarousel,
});
